/**
 * Decision support for the WiFi-cellular mismatch review flow.
 *
 * The reviewer's rule of thumb, made explicit: when the centre of the WiFi
 * observations falls inside the cloud of harvested towers that share the same
 * LAC, the cell tower really is over there and its external location is wrong -
 * so the tower should be pinned at that WiFi centre. When the WiFi centre sits
 * outside the LAC cloud there is no confident answer and the decision is left
 * to the reviewer.
 *
 * Kept free of React and i18n so the rule stays testable on its own.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Distance in metres the WiFi centre may sit outside the LAC hull and still count as overlapping. */
export const LAC_HULL_BUFFER_METERS = 2000;

/** Below this many harvested towers the LAC cloud has no meaningful shape. */
const MIN_TOWERS_FOR_HULL = 3;

const EARTH_RADIUS_METERS = 6371008.8;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/** Great-circle distance in metres. */
export const distanceMeters = (a: LatLng, b: LatLng): number => {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Unweighted mean position. Safe for a single LAC - no antimeridian handling needed. */
export const centroidOf = (points: LatLng[]): LatLng | null => {
  if (points.length === 0) return null;
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
};

/**
 * Convex hull via monotone chain, returned counter-clockwise without a repeated
 * closing vertex. Fewer than 3 distinct input points yields those points as-is.
 */
export const convexHull = (points: LatLng[]): LatLng[] => {
  const unique = Array.from(
    new Map(points.map((p) => [`${p.lat},${p.lng}`, p])).values()
  ).sort((a, b) => (a.lng === b.lng ? a.lat - b.lat : a.lng - b.lng));

  if (unique.length < 3) return unique;

  // Positive when o->a->b turns counter-clockwise
  const cross = (o: LatLng, a: LatLng, b: LatLng) =>
    (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);

  const build = (sorted: LatLng[]): LatLng[] => {
    const chain: LatLng[] = [];
    for (const point of sorted) {
      while (
        chain.length >= 2 &&
        cross(chain[chain.length - 2], chain[chain.length - 1], point) <= 0
      ) {
        chain.pop();
      }
      chain.push(point);
    }
    return chain;
  };

  const lower = build(unique);
  const upper = build([...unique].reverse());
  // Drop each chain's last point - it is the other chain's first point
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

/** Ray casting. Points exactly on an edge are not guaranteed either way; the buffer covers them. */
export const isPointInPolygon = (point: LatLng, polygon: LatLng[]): boolean => {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const yi = polygon[i].lat;
    const xi = polygon[i].lng;
    const yj = polygon[j].lat;
    const xj = polygon[j].lng;
    const straddles = yi > point.lat !== yj > point.lat;
    if (
      straddles &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * Shortest distance in metres from a point to a polygon's boundary, using an
 * equirectangular projection centred on the point. Accurate well beyond the few
 * kilometres this rule cares about.
 */
export const distanceToPolygonMeters = (
  point: LatLng,
  polygon: LatLng[]
): number => {
  if (polygon.length === 0) return Number.POSITIVE_INFINITY;
  if (polygon.length === 1) return distanceMeters(point, polygon[0]);

  const metersPerDegreeLat = 110574;
  const metersPerDegreeLng = 111320 * Math.cos(toRadians(point.lat));
  const project = (p: LatLng) => ({
    x: (p.lng - point.lng) * metersPerDegreeLng,
    y: (p.lat - point.lat) * metersPerDegreeLat,
  });

  let shortest = Number.POSITIVE_INFINITY;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = project(polygon[j]);
    const b = project(polygon[i]);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    // Clamp the projection onto the segment so endpoints are handled too
    const t =
      lengthSquared === 0
        ? 0
        : Math.max(0, Math.min(1, -(a.x * dx + a.y * dy) / lengthSquared));
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    shortest = Math.min(shortest, Math.sqrt(cx * cx + cy * cy));
  }
  return shortest;
};

export type RecommendationReason =
  /** WiFi centre overlaps the LAC hull - the tower belongs at that centre. */
  | "WIFI_CENTRE_IN_LAC_CLOUD"
  /** WiFi centre sits clear of the LAC hull - not decidable from the LAC cloud. */
  | "WIFI_CENTRE_OUTSIDE_LAC_CLOUD"
  /** Too few harvested towers in this LAC to form a cloud. */
  | "NO_LAC_DATA"
  /** No usable WiFi position on the mismatches. */
  | "NO_WIFI_DATA"
  /** COUNTRY mismatches carry no resolved tower location, so the rule does not apply. */
  | "COUNTRY_MISMATCH";

export interface Recommendation {
  /** Decision radio to pre-select: 3 = use new cell tower location, 1 = delay. */
  option: 1 | 3;
  /** Marker position for option 3; null whenever the decision is left open. */
  location: { latitude: number; longitude: number } | null;
  reason: RecommendationReason;
  /** WiFi centre, exposed so the caller can show how far outside the cloud it fell. */
  wifiCentre: LatLng | null;
  /** Distance in metres from the WiFi centre to the LAC hull; 0 when inside it. */
  distanceToLacCloudMeters: number | null;
  /** Harvested towers that formed the cloud. */
  lacTowerCount: number;
}

/** The subset of a mismatch this rule reads. */
export interface RecommendationInput {
  type?: string;
  wifiLat?: number;
  wifiLng?: number;
  wifiHull?: { x: number; y: number }[];
  lacTowers?: { lat?: number; lng?: number }[];
}

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** Every WiFi position across the mismatches, falling back to hull vertices. */
const collectWifiPoints = (items: RecommendationInput[]): LatLng[] => {
  const points: LatLng[] = [];
  items.forEach((item) => {
    if (isNumber(item.wifiLat) && isNumber(item.wifiLng)) {
      points.push({ lat: item.wifiLat, lng: item.wifiLng });
    } else if (item.wifiHull) {
      item.wifiHull
        .filter((c) => isNumber(c.x) && isNumber(c.y))
        .forEach((c) => points.push({ lat: c.x, lng: c.y }));
    }
  });
  return points;
};

const emptyRecommendation = (reason: RecommendationReason): Recommendation => ({
  option: 1,
  location: null,
  reason,
  wifiCentre: null,
  distanceToLacCloudMeters: null,
  lacTowerCount: 0,
});

/**
 * Decide what the resolve dialog should pre-select for one cell tower's
 * mismatches. Never returns a destructive option - the worst case is "delay",
 * which leaves the call to the reviewer.
 */
export const recommendDecision = (
  items: RecommendationInput[]
): Recommendation => {
  if (items.length === 0) return emptyRecommendation("NO_WIFI_DATA");
  if (items[0].type === "COUNTRY")
    return emptyRecommendation("COUNTRY_MISMATCH");

  const wifiCentre = centroidOf(collectWifiPoints(items));
  if (!wifiCentre) return emptyRecommendation("NO_WIFI_DATA");

  const lacPoints = (items[0].lacTowers || [])
    .filter((t) => isNumber(t.lat) && isNumber(t.lng))
    .map((t) => ({ lat: t.lat as number, lng: t.lng as number }));

  if (lacPoints.length < MIN_TOWERS_FOR_HULL) {
    return {
      ...emptyRecommendation("NO_LAC_DATA"),
      wifiCentre,
      lacTowerCount: lacPoints.length,
    };
  }

  const hull = convexHull(lacPoints);
  const inside = isPointInPolygon(wifiCentre, hull);
  const distance = inside ? 0 : distanceToPolygonMeters(wifiCentre, hull);
  const overlaps = inside || distance <= LAC_HULL_BUFFER_METERS;

  return {
    option: overlaps ? 3 : 1,
    location: overlaps
      ? { latitude: wifiCentre.lat, longitude: wifiCentre.lng }
      : null,
    reason: overlaps
      ? "WIFI_CENTRE_IN_LAC_CLOUD"
      : "WIFI_CENTRE_OUTSIDE_LAC_CLOUD",
    wifiCentre,
    distanceToLacCloudMeters: distance,
    lacTowerCount: lacPoints.length,
  };
};
